// Session lifecycle shared by the whole app: sign-out, session expiry, and
// keeping every open tab in step.
//
// The refresh token is an httpOnly cookie (set by the backend on login and
// every rotation), so this module never sees it. Revoking it means calling
// /account/logout/, which blacklists it and clears the cookie.

import config from "../config";
import { logout } from "./features/auth/authSlice";
import { clearPendingEligibilityForm } from "../utils/pendingEligibilityForm";
import {
  buildLoginUrl,
  currentPath,
  isProtectedPath,
  safeNextPath,
} from "../utils/authRedirect";

// Join onto the API base without doubling or dropping the slash
// (VITE_BASE_URL ends in "/api/").
export const apiUrl = (path) =>
  `${config.baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

// Latest access token, written synchronously so another tab can pick it up
// the moment it's issued instead of spending the (single-use) refresh cookie.
const SHARED_ACCESS_KEY = "uniedu:access";

export const shareAccessToken = (token) => {
  try {
    if (token) localStorage.setItem(SHARED_ACCESS_KEY, token);
    else localStorage.removeItem(SHARED_ACCESS_KEY);
  } catch {
    // Storage blocked — tabs just refresh independently.
  }
};

export const readSharedAccessToken = () => {
  try {
    return localStorage.getItem(SHARED_ACCESS_KEY);
  } catch {
    return null;
  }
};

const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("uniedu-auth")
    : null;

const post = (message) => {
  try {
    channel?.postMessage(message);
  } catch {
    // Channel closed — other tabs will notice on their next request.
  }
};

let storeRef = null;
let leaving = false;

// Blacklist the refresh token and clear its cookie. keepalive lets the request
// finish even though we navigate away immediately. `legacyRefresh` covers
// sessions from before the cookie existed, which still hold the token in state.
const revokeOnServer = (legacyRefresh) => {
  try {
    fetch(apiUrl("account/logout/"), {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(legacyRefresh ? { refresh: legacyRefresh } : {}),
    }).catch(() => {});
  } catch {
    // Nothing more we can do; the token still expires on its own.
  }
};

const clearLocalSession = (dispatch) => {
  try {
    localStorage.clear();
  } catch {
    // ignore
  }
  // Personal data from the eligibility form lives in sessionStorage.
  clearPendingEligibilityForm();
  dispatch(logout());
};

// Where to go once signed out. Protected pages → login (with a way back);
// public pages → stay, reloaded, so no signed-in UI or cached data survives.
const leave = (reason) => {
  if (isProtectedPath()) {
    window.location.replace(buildLoginUrl(currentPath(), reason));
  } else {
    window.location.reload();
  }
};

const endSession = ({ dispatch, getState }, reason) => {
  if (leaving) return;
  leaving = true;
  revokeOnServer(getState?.().auth?.refreshToken);
  clearLocalSession(dispatch);
  post({ type: "logout", reason });
};

/** The user chose to sign out (any logout button). */
export const signOut = () => {
  if (!storeRef) return;
  endSession(storeRef);
  window.location.replace("/sign-in");
};

/** The session can't be renewed any more (called by baseApi). */
export const endExpiredSession = (api) => {
  if (leaving) return;
  endSession(api, "expired");
  leave("expired");
};

/** Tell other tabs a (possibly different) user just signed in. */
export const announceLogin = () => post({ type: "login" });

/** Wire up cross-tab sync. Called once from store.js. */
export const initAuthSync = (store) => {
  storeRef = store;
  if (!channel) return;
  channel.onmessage = ({ data }) => {
    if (data?.type === "logout") {
      if (leaving) return;
      leaving = true;
      // The other tab already revoked the token and cleared storage.
      store.dispatch(logout());
      leave(data.reason);
    } else if (data?.type === "login") {
      // Pick up the new session. From the login page, go where it was headed.
      if (window.location.pathname.startsWith("/sign-in")) {
        const next = safeNextPath(
          new URLSearchParams(window.location.search).get("next")
        );
        window.location.replace(next || "/");
      } else {
        window.location.reload();
      }
    }
  };
};
